
import axios from 'axios';
import * as turf from "@turf/turf";
import { useState, useRef, useEffect, useCallback } from "react"
import Select from 'react-select'
import Table from 'react-bootstrap/Table';
import { formatDate } from '../CustomDateTools';
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    Popup,
    Source,
    Layer,
    useMap
} from 'react-map-gl/maplibre';




const GoldFinderGeoserver = axios.create({
    baseURL: process.env.REACT_APP_GOLD_FINDER_GEOSERVER
})

let DhAbortController = new AbortController();
let lastRequestTime = 0;
const REQUEST_DEBOUNCE_MS = 300;
const REQUEST_TIMEOUT_MS = 30000;
let pendingRequest = null;

// Simple debounce utility
function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/*
    ppblt: 9999,
    ppbmt: 30,
    maxresults: 500,
    type: sampleTypeDataPreSelected,
    nullValues: false,
*/

export function queryAssayAnumberWFS(anumber) {
    const ecqlFilter = ` anumber = ${anumber}`


    DhAbortController.abort();

    return GoldFinderGeoserver.get("/ne/ows?", {
        signal: DhAbortController.signal,
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_au_soil_assays',
            maxFeatures: 99999,

            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            cql_filter: ecqlFilter
        }
    })
}

export function queryDHWFS(bboxString, setStateGeoJSON, dhFilters) {
    // Debounce requests to prevent overwhelming the server
    const now = Date.now();
    if (now - lastRequestTime < REQUEST_DEBOUNCE_MS) {
        return;
    }
    lastRequestTime = now;

    // Cancel any pending request only if there's actually one pending
    if (pendingRequest) {
        DhAbortController.abort();
        DhAbortController = new AbortController();
    }

    const ecqlFilter = `
        BBOX(geom,${bboxString})
        and
        au_gpt BETWEEN ${dhFilters.gptmt} AND ${dhFilters.gptlt} 
        and todepth <=${dhFilters.maxDepth}
        `


    // Mark that we have a pending request
    pendingRequest = GoldFinderGeoserver.get("/ne/ows?", {
        signal: DhAbortController.signal,
        timeout: REQUEST_TIMEOUT_MS,
        params: {
            service: 'WFS',
            version: '1.1.0',
            request: 'GetFeature',
            typeNames: 'ne:wa_au_drill_holes',
            maxFeatures: 99999, // Reduced from 99999 to improve performance
            outputFormat: 'application/json',
            srs: 'EPSG:4326',
            cql_filter: ecqlFilter
        }
    });

    pendingRequest
        .then((response) => {
            pendingRequest = null; // Clear pending request

            if (DhAbortController.signal.aborted) {
                return; // Request was cancelled
            }

            // Process the data more efficiently
            const features = response.data.features || [];
            const processedFeatures = new Map();

            // Use Map for O(1) lookups instead of array.find()
            features.forEach(feature => {
                const id = feature.properties.dhgeochemid;
                if (processedFeatures.has(id)) {
                    const existing = processedFeatures.get(id);
                    existing.properties.au_gpt = +existing.properties.au_gpt + +feature.properties.au_gpt;
                } else {
                    processedFeatures.set(id, feature);
                }
            });

            const result = {
                ...response.data,
                features: Array.from(processedFeatures.values())
            };

            setStateGeoJSON(result);
        })
        .catch((error) => {
            pendingRequest = null; // Clear pending request

            if (error.name === 'AbortError' || DhAbortController.signal.aborted || error.message === 'canceled') {
                return;
            }

            console.error('Drill hole WFS request failed:', error);
            console.error('Error details:', {
                message: error.message,
                status: error.response?.status,
                statusText: error.response?.statusText,
                url: error.config?.url,
                params: error.config?.params
            });

            // Only set empty data on actual errors, not cancellations
            setStateGeoJSON({
                type: "FeatureCollection",
                features: []
            });
        });
}


export function createSingleDrillHolePopupTable(dhFeature, lngLat, setClosed, setModalShow, setReportedAssay) {
    return <Popup latitude={lngLat.lat} longitude={lngLat.lng}
        anchor="bottom"
        onClose={() => setClosed(false)}>
        <Table striped bordered className="mb-1" >
            <tbody>
                <tr>
                    <td>ID:</td>
                    <td>
                        {dhFeature.properties.dhassayid}
                        {(dhFeature.properties.reported_sample_id !== undefined ?
                            <FontAwesomeIcon title="This Sample Has been reported as inaccurate or incorrect." style={{ marginLeft: 3 }} icon={faCircleExclamation} />
                            : "")}
                    </td>
                </tr>
                <tr>
                    <td>Au g/t:</td>
                    <td>{dhFeature.properties.au_gpt.toFixed(2)}</td>
                </tr>
                <tr>
                    <td>From Depth (Sample):</td>
                    <td>{dhFeature.properties.fromdepth}</td>
                </tr>
                <tr>
                    <td>To Depth (Sample):</td>
                    <td>{dhFeature.properties.todepth}</td>
                </tr>
                <tr>
                    <td>Max Depth (Hole):</td>
                    <td>{dhFeature.properties.maxdepth}</td>
                </tr>
                <tr>
                    <td>Hole Type:</td>
                    <td>{dhFeature.properties.holetype}</td>
                </tr>
                <tr>
                    <td>A Number:</td>
                    <td>
                        <a target="_blank" href={`https://wamex.dmp.wa.gov.au/Wamex/Search/ReportDetails?ANumber=${dhFeature.properties.anumber}`}>
                            {dhFeature.properties.anumber}
                        </a>
                        {(dhFeature.properties.reported_sample_anumber_id !== undefined ?
                            <FontAwesomeIcon title="The samples related to this A Number have been reported as inaccurate or incorrect." style={{ marginLeft: 3 }} icon={faCircleExclamation} />
                            : "")}
                    </td>
                </tr>
                <tr>
                    <td>Company Sample ID:</td>
                    <td>{dhFeature.properties.companysampleid}</td>
                </tr>
            </tbody>
        </Table>
        {
            (
                dhFeature.properties.reported_sample_anumber_id === undefined && dhFeature.properties.reported_sample_id === undefined ?
                    <button className="btn btn-secondary btn-sm" style={{ fontSize: 10 }} onClick={() => { setModalShow(true); setReportedAssay(dhFeature.properties) }}>Report Sample</button>
                    : ""
            )
        }
    </Popup >
}

function AssayLayers({ assayMarkerColourSettings, overlayVisibility }) {

    const orderedAssayMarkerArray = assayMarkerColourSettings.toSorted((a, b) => a.moreThan - b.moreThan);


    const rows = [];

    // push  white under
    for (let i = 0; i < orderedAssayMarkerArray.length; i++) {
        const next = i + 1;
        const layerId = orderedAssayMarkerArray[i].id + '-dh';

        const layerConfig = {
            id: layerId,
            'source': 'dhsamples',
            type: 'symbol',
            minzoom: 10.6,
            filter: ['all',
                ['>=', 'au_gpt', orderedAssayMarkerArray[i].moreThan],
                ['<', 'au_gpt', i !== orderedAssayMarkerArray.length - 1 ? orderedAssayMarkerArray[next].moreThan : 9999],
                //['!=', 'au_ppb', null]
            ],
            layout: {
                visibility: `${overlayVisibility.dh ? "visible" : "none"}`,
                'icon-image': `triangle-${orderedAssayMarkerArray[i].id}`,
                'icon-size': 1.2,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-anchor': 'center'
            },
            paint: {
                'icon-opacity': 1.0
            }
        }

        rows.push(<Layer key={layerId} {...layerConfig} />)
    }
    return rows;
}


export function DrillHoleOverlayMapLibre({ overlayVisibility, drillHoleMarkerColourSettings, drillHoleGeoJson, setDrillHoleGeoJson, drillHoleFilters }) {
    const emptyGeoJson = {
        "type": "FeatureCollection",
        "name": "assay_samples",
        "crs": { "type": "name", "properties": { "name": "urn:ogc:def:crs:EPSG::4326" } },
        "features": []
    };

    const { goldfindermap } = useMap(); // `mymap` is the id in <Map id="mymap" />

    // Add custom triangle SVG images to map for each color
    useEffect(() => {
        if (!goldfindermap || !drillHoleMarkerColourSettings) return;

        // Create triangle using canvas and convert to PNG (larger clickable area)
        drillHoleMarkerColourSettings.forEach((setting) => {
            const canvas = document.createElement('canvas');
            canvas.width = 24;
            canvas.height = 24;
            const ctx = canvas.getContext('2d');

            // Clear canvas with transparent background
            ctx.clearRect(0, 0, 24, 24);

            // Draw white border triangle first (slightly larger)
            ctx.beginPath();
            ctx.moveTo(12, 20); // bottom point
            ctx.lineTo(4, 4);   // top left
            ctx.lineTo(20, 4);  // top right
            ctx.closePath();
            ctx.fillStyle = '#ffffff';
            ctx.fill();

            // Draw colored triangle on top
            ctx.beginPath();
            ctx.moveTo(12, 19); // bottom point
            ctx.lineTo(5, 5);   // top left
            ctx.lineTo(19, 5);  // top right
            ctx.closePath();
            ctx.fillStyle = setting.color;
            ctx.fill();

            const imageId = `triangle-${setting.id}`;

            // Convert canvas to PNG data URL
            const dataURL = canvas.toDataURL('image/png');
            const img = new Image();
            img.onload = () => {
                // Remove existing image if it exists to allow updates
                if (goldfindermap.hasImage(imageId)) {
                    goldfindermap.removeImage(imageId);
                }
                goldfindermap.addImage(imageId, img);
            };
            img.src = dataURL;
        });

        // Add white triangle for background using canvas (no longer needed since we include border in main triangles)
        // Remove this background layer since each triangle now has its own white border

        // Cleanup function to remove images when component unmounts or settings change
        return () => {
            if (goldfindermap) {
                drillHoleMarkerColourSettings.forEach((setting) => {
                    const imageId = `triangle-${setting.id}`;
                    if (goldfindermap.hasImage(imageId)) {
                        goldfindermap.removeImage(imageId);
                    }
                });
            }
        };

    }, [goldfindermap, drillHoleMarkerColourSettings]);

    useEffect(() => {
        if (!goldfindermap) return undefined;

        const handleMapUpdate = () => {
            if (goldfindermap.getZoom() > 10.6) {
                const bounds = goldfindermap.getBounds();
                const wfsbbox = [bounds.getSouthWest().lng, bounds.getSouthWest().lat, bounds.getNorthEast().lng, bounds.getNorthEast().lat].join();
                queryDHWFS(wfsbbox, setDrillHoleGeoJson, drillHoleFilters);
            } else {
                // Clear data when zoomed out too far
                setDrillHoleGeoJson({
                    type: "FeatureCollection",
                    features: []
                });
            }
        };

        // Initial load
        handleMapUpdate();

        // Listen for map movement events with debouncing
        const debouncedUpdate = debounce(handleMapUpdate, 800);
        goldfindermap.on('moveend', debouncedUpdate);
        goldfindermap.on('zoomend', handleMapUpdate); // No debounce for zoom as it's less frequent

        return () => {
            goldfindermap.off('moveend', debouncedUpdate);
            goldfindermap.off('zoomend', handleMapUpdate);
        };
    }, [drillHoleFilters, goldfindermap, setDrillHoleGeoJson])



    return (overlayVisibility.dh && <Source id="dhsamples" type="geojson" data={drillHoleGeoJson}>
        <AssayLayers overlayVisibility={overlayVisibility} assayMarkerColourSettings={drillHoleMarkerColourSettings} />
    </Source>)
}